/**
 * Contact Address Service
 *
 * Handles all contact address operations including CRUD
 * and bulk operations.
 *
 * @module ContactAddressService
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { APP_SCHEMA_ENDPOINTS, buildUrlWithParams } from '../api';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type {
  ContactAddressCreate,
  ContactAddressRead,
  ContactAddressUpdate,
  ContactAddressWithDetails,
} from '../models/contact-address.types';

/**
 * Base API Service helper methods
 */
abstract class BaseContactService {
  protected http = inject(HttpClient);

  protected createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  protected createParams(query: Record<string, unknown>): Record<string, string> {
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        params[key] = String(value);
      }
    }
    return params;
  }

  protected handleError = (error: unknown): Observable<never> => {
    console.error('❌ ContactAddressService Error:', error);
    if (error && typeof error === 'object' && 'error' in error) {
      console.error('❌ Error details:', (error as any).error);
    }
    throw error;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ContactAddressService extends BaseContactService {
  // ========================
  // CONTACT ADDRESS CRUD OPERATIONS
  // ========================

  /**
   * Get contact addresses by contact ID (SQLModel format)
   * Returns ContactAddressRead[] from /api/app/contact_addresses/search?fk_contact_id={contact_id}
   * Note: Uses the generated search endpoint, returns junction table data only
   */
  getContactAddresses(contactId: number): Observable<ContactAddressRead[]> {
    const url = buildUrlWithParams(`${APP_SCHEMA_ENDPOINTS.contactAddresses.list}search`, {
      fk_contact_id: contactId,
    });

    return this.http
      .get<{ contact_addresses: ContactAddressRead[]; total: number }>(url, {
        headers: this.createHeaders(),
      })
      .pipe(
        map((response) => response.contact_addresses),
        catchError(this.handleError)
      );
  }

  /**
   * Get specific contact address by ID (SQLModel format)
   * Returns ContactAddressRead from /api/app/contact_addresses/{contact_address_id}
   */
  getContactAddress(contactAddressId: number): Observable<ContactAddressRead> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.contactAddresses.getById(contactAddressId));

    return this.http
      .get<ContactAddressRead>(url, { headers: this.createHeaders() })
      .pipe(catchError(this.handleError));
  }

  /**
   * Create contact address (SQLModel format)
   */
  createContactAddress(addressData: ContactAddressCreate): Observable<ContactAddressRead> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.contactAddresses.create);

    console.log('🔍 Creating contact_address with data:', JSON.stringify(addressData, null, 2));
    console.log('🔍 URL:', url);

    return this.http
      .post<ContactAddressRead>(url, addressData, {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Update contact address (SQLModel format)
   */
  updateContactAddress(
    contactAddressId: number,
    addressData: ContactAddressUpdate
  ): Observable<ContactAddressRead> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.contactAddresses.update(contactAddressId));

    return this.http
      .put<ContactAddressRead>(url, addressData, {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Delete contact address (SQLModel format)
   */
  deleteContactAddress(contactAddressId: number): Observable<void> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.contactAddresses.delete(contactAddressId));

    return this.http
      .delete<void>(url, { headers: this.createHeaders() })
      .pipe(catchError(this.handleError));
  }

  /**
   * Get all contact addresses with pagination (for contact list view)
   * GET /api/app/contact_addresses/
   */
  getAllContactAddresses(params?: {
    skip?: number;
    limit?: number;
  }): Observable<ContactAddressRead[]> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.contactAddresses.list, params || {});

    return this.http
      .get<ContactAddressRead[]>(url, { headers: this.createHeaders() })
      .pipe(catchError(this.handleError));
  }
}
